import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./PP.css";

/* ── AOS lazy-load ── */
let aosLoaded = false;
function initAOS() {
  if (aosLoaded) return;
  aosLoaded = true;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://unpkg.com/aos@2.3.4/dist/aos.css";
  document.head.appendChild(link);
  const script = document.createElement("script");
  script.src = "https://unpkg.com/aos@2.3.4/dist/aos.js";
  script.onload = () => window.AOS?.init({ once: true, duration: 600, easing: "ease-out-cubic", offset: 60 });
  document.head.appendChild(script);
}

const API = "/api";
const API_ORIGIN = (() => {
  const raw = process.env.REACT_APP_API_URL || "http://localhost:3000";
  return raw.replace(/\/api$/, "").replace(/\/$/, "");
})();

const getToken = () => localStorage.getItem("th_token") || localStorage.getItem("token") || "";
const getUser = () => {
  try {
    const r = localStorage.getItem("th_user") || localStorage.getItem("user");
    return r ? JSON.parse(r) : null;
  } catch { return null; }
};
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

function resolvePhoto(raw) {
  if (!raw) return null;
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  return raw;
}

function resolveApiFileUrl(raw) {
  if (!raw) return null;
  if (raw.startsWith("http") || raw.startsWith("blob:")) return raw;
  return `${API_ORIGIN}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function tryParse(v, fb = []) {
  try { return typeof v === "string" ? JSON.parse(v) : (v || fb); } catch { return fb; }
}

function getPublicProfileUrl(username) {
  if (!username || typeof window === "undefined") return "";
  return `${window.location.origin}/candidate/${username}`;
}

function getLevelInfo(pts) {
  if (pts >= 500) return { title: "Champion",  emoji: "👑", color: "#92400e", bg: "#fef3c7" };
  if (pts >= 400) return { title: "Performer", emoji: "🔥", color: "#92400e", bg: "#fef3c7" };
  if (pts >= 250) return { title: "Achiever",  emoji: "⭐", color: "#6d28d9", bg: "#ede9fe" };
  if (pts >= 100) return { title: "Explorer",  emoji: "🧭", color: "#1d4ed8", bg: "#dbeafe" };
  if (pts >= 50)  return { title: "Starter",   emoji: "🎁", color: "#16803c", bg: "#dcfce7" };
  return                  { title: "Rookie",    emoji: "🚀", color: "#5f5e5a", bg: "#f3f2ef" };
}

function getLevelProgress(pts) {
  const thresholds = [0, 50, 100, 250, 400, 500];
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (pts >= thresholds[i]) {
      const next = thresholds[i + 1] || thresholds[i];
      const range = next - thresholds[i] || 1;
      return Math.min(100, Math.round(((pts - thresholds[i]) / range) * 100));
    }
  }
  return 0;
}

/* ── Animated counter ── */
function AnimCount({ to, duration = 1100 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [to, duration]);
  return <span>{val}</span>;
}

/* ── Skill pill with stagger ── */
function SkillPill({ skill, delay }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <span className={`pcp-skill-pill ${show ? "pcp-skill-pill--in" : ""}`}>{skill}</span>
  );
}

/* ── Scroll reveal ── */
function RevealSection({ children, className = "", delay = 0, id }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} id={id} className={`pcp-reveal ${visible ? "pcp-reveal--in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ── Toast ── */
function Toast({ msg, type = "success", onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3500); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className={`pcp-toast ${type === "error" ? "pcp-toast--error" : ""}`}>
      <span>{type === "error" ? "✕" : "✓"}</span>
      {msg}
    </div>
  );
}

/* ── Recommendation card ── */
function RecoCard({ reco, delay, onEdit, onDelete }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [delay]);
  const initials = (reco.recommender_name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div className={`pcp-reco-card ${show ? "pcp-reco-card--in" : ""}`}>
      <div className="pcp-reco-card__accent" />
      <span className="pcp-reco-card__quote-mark">"</span>
      <p className="pcp-reco-card__text">{reco.comment}</p>
      <div className="pcp-reco-card__author">
        <div className="pcp-reco-card__avatar">{initials}</div>
        <div>
          <div className="pcp-reco-card__name">{reco.recommender_name || "Anonymous"}</div>
          {reco.recommender_position && (
            <div className="pcp-reco-card__pos">{reco.recommender_position}</div>
          )}
          <div className="pcp-reco-card__date">
            {reco.created_at
              ? new Date(reco.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
              : ""}
          </div>
        </div>
      </div>
      {(reco.can_edit || reco.can_delete) && (
        <div className="pcp-reco-card__actions">
          {reco.can_edit && (
            <button type="button" className="pcp-btn pcp-btn--ghost pcp-btn--sm" onClick={() => onEdit?.(reco)}>
              Edit
            </button>
          )}
          {reco.can_delete && (
            <button type="button" className="pcp-btn pcp-btn--outline pcp-btn--sm" onClick={() => onDelete?.(reco)}>
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Write / Edit Testimonial Modal ── */
function WriteRecoModal({
  candidateId, candidateName, onClose, onSubmitted,
  recommendationId = null, initialComment = "", initialPosition = "",
}) {
  const isEdit = !!recommendationId;
  const [comment, setComment] = useState(initialComment);
  const [position, setPosition] = useState(initialPosition);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!comment.trim() || comment.trim().length < 20) {
      setError("Please write at least 20 characters.");
      return;
    }
    const role = String(getUser()?.role || "").toLowerCase();
    if (role !== "employer" && role !== "recruiter") {
      setError("Only recruiter accounts can submit testimonials.");
      return;
    }
    setLoading(true); setError("");
    try {
      const payload = { comment: comment.trim(), reviewer_position: position.trim() || null };
      if (isEdit) {
        await axios.patch(`${API}/candidate/recommendations/${recommendationId}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/candidate/${candidateId}/recommendations`, payload, { headers: authHeaders() });
      }
      onSubmitted(); onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="pcp-modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pcp-modal">
        <button className="pcp-modal__close" onClick={onClose}>✕</button>
        <div className="pcp-modal__icon">✍️</div>
        <h3 className="pcp-modal__title">
          {isEdit ? `Edit testimonial` : `Write a testimonial for ${candidateName}`}
        </h3>
        <p className="pcp-modal__sub">Share your experience working with or knowing this person.</p>

        <div className="pcp-modal__field">
          <label>Your role / position <span style={{ color: "var(--muted)", fontSize: 11 }}>(optional)</span></label>
          <input
            className="pcp-modal__input"
            placeholder="e.g. Senior Manager at Infosys"
            value={position}
            onChange={e => setPosition(e.target.value)}
          />
        </div>
        <div className="pcp-modal__field">
          <label>Testimonial <span style={{ color: "#e24b4a" }}>*</span></label>
          <textarea
            className="pcp-modal__textarea"
            placeholder={`What makes ${candidateName} stand out professionally…`}
            value={comment}
            maxLength={800}
            onChange={e => { setComment(e.target.value); setError(""); }}
          />
          <div className="pcp-modal__count">{comment.length} / 800</div>
        </div>

        {error && <div className="pcp-modal__error">{error}</div>}

        <div className="pcp-modal__actions">
          <button className="pcp-modal__btn pcp-modal__btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pcp-modal__btn pcp-modal__btn--primary" onClick={handleSubmit} disabled={loading}>
            {loading ? "Saving…" : isEdit ? "Save changes →" : "Submit testimonial →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WriteRatingModal({ candidateId, candidateName, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    skills_rating: 5,
    experience_rating: 5,
    communication_rating: 5,
    problem_solving_rating: 5,
    attitude_rating: 5,
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ratingFields = [
    ["skills_rating", "Skills"],
    ["experience_rating", "Experience"],
    ["communication_rating", "Communication"],
    ["problem_solving_rating", "Problem Solving"],
    ["attitude_rating", "Attitude"],
  ];

  const overall = Math.round(((Number(form.skills_rating) + Number(form.experience_rating) + Number(form.communication_rating) + Number(form.problem_solving_rating) + Number(form.attitude_rating)) / 5) * 10) / 10;

  const submit = async () => {
    const role = String(getUser()?.role || "").toLowerCase();
    if (role !== "employer" && role !== "recruiter") {
      setError("Only recruiter accounts can submit ratings.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = { ...form };
      await axios.post(`${API}/candidate/${candidateId}/ratings`, payload, { headers: authHeaders() });
      onSubmitted();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to submit rating.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pcp-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pcp-modal pcp-modal--rating">
        <button className="pcp-modal__close" onClick={onClose}>✕</button>
        <div className="pcp-modal__icon">⭐</div>
        <h3 className="pcp-modal__title">Rate {candidateName}</h3>
        <p className="pcp-modal__sub">Give a structured recruiter rating based on the candidate's professional strengths.</p>

        <div className="pcp-rating-grid">
          {ratingFields.map(([key, label]) => (
            <label key={key} className="pcp-rating-field">
              <span>{label}</span>
              <select
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                className="pcp-modal__input"
              >
                {[5, 4, 3, 2, 1].map((v) => (
                  <option key={v} value={v}>{v} / 5</option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div className="pcp-rating-summary">Overall rating: <strong>{overall} / 5</strong></div>

        <div className="pcp-modal__field">
          <label>Note <span style={{ color: "var(--muted)", fontSize: 11 }}>(optional)</span></label>
          <textarea
            className="pcp-modal__textarea"
            placeholder="Add a short note about this candidate's strengths..."
            value={form.note}
            maxLength={500}
            onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
          />
        </div>

        {error && <div className="pcp-modal__error">{error}</div>}

        <div className="pcp-modal__actions">
          <button className="pcp-modal__btn pcp-modal__btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pcp-modal__btn pcp-modal__btn--primary" onClick={submit} disabled={loading}>
            {loading ? "Saving…" : "Submit rating →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StarRow({ score, max = 5 }) {
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {Array.from({ length: max }).map((_, i) => {
        const filled = score >= i + 1;
        const half = !filled && score >= i + 0.5;
        return (
          <span
            key={i}
            style={{
              fontSize: 18,
              color: filled || half ? "#4a5e20" : "#d1d5db",
              lineHeight: 1,
            }}
          >
            {filled ? "★" : half ? "⯨" : "☆"}
          </span>
        );
      })}
    </div>
  );
}
/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
export default function PublicCandidateProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState(null);
  const [recos, setRecos] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [ratingSummary, setRatingSummary] = useState({ total: 0, average_overall: 0 });
  const [complaints, setComplaints] = useState([]);
  const [complaintSummary, setComplaintSummary] = useState({ total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
  const [complaintLoading, setComplaintLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [recoModal, setRecoModal] = useState(false);
  const [editingReco, setEditingReco] = useState(null);
  const [ratingModal, setRatingModal] = useState(false);
  const [activeSection, setActiveSection] = useState("about");

  const currentUser = getUser();
  const isLoggedIn = !!getToken();
  const userRole = String(currentUser?.role || "").toLowerCase();
  const isEmployer = userRole === "employer" || userRole === "recruiter";
  const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";
  const isOwnProfile = currentUser?.username === username || currentUser?.id === profile?.id;
  const canRecruiterReview = isLoggedIn && isEmployer && !isOwnProfile;
  const canSeeComplaintDetails = isOwnProfile || isAdmin;
  const focusedTicketId = Number(new URLSearchParams(location.search).get("ticket") || 0);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    initAOS();
    if (!username) { setError("Profile not found."); setLoading(false); return; }
    load();
  }, [username]); // eslint-disable-line

  useEffect(() => {
    // Refresh AOS after content loads
    if (!loading && profile) {
      setTimeout(() => window.AOS?.refresh?.(), 300);
    }
  }, [loading, profile]);

  async function load() {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/candidate/public/${username}`);
      const p = data.data || data;
      p.skills    = tryParse(p.skills, []);
      p.locations = tryParse(p.locations, []);
      p.courses   = tryParse(p.courses, []);
      p.projects  = tryParse(p.projects, []);
      p.education = tryParse(p.education, []);
      setProfile(p);
      await loadComplaints(p.id);
      loadRecos(p.id);
      loadRatings(p.id);
    } catch (e) {
      setError(e?.response?.data?.error || "Profile not found.");
    } finally { setLoading(false); }
  }

  async function loadComplaints(candidateId) {
    if (!candidateId) return;
    setComplaintLoading(true);
    try {
      const headers = isLoggedIn ? authHeaders() : {};
      const { data } = await axios.get(`${API}/candidate/${candidateId}/complaints`, { headers });
      setComplaints(Array.isArray(data.complaints) ? data.complaints : []);
      setComplaintSummary(data.summary || { total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
    } catch {
      setComplaints([]);
      setComplaintSummary({ total: 0, visible: 0, open: 0, resolved: 0, hidden: 0 });
    } finally {
      setComplaintLoading(false);
    }
  }

  async function loadRecos(candidateId) {
    try {
      const { data } = await axios.get(`${API}/candidate/${candidateId}/recommendations`);
      setRecos(data.recommendations || data.data || []);
    } catch { setRecos([]); }
  }

  async function loadRatings(candidateId) {
    try {
      const { data } = await axios.get(`${API}/candidate/${candidateId}/ratings`);
      setRatings(data.ratings || []);
      setRatingSummary(data.summary || { total: 0, average_overall: 0 });
    } catch {
      setRatings([]);
      setRatingSummary({ total: 0, average_overall: 0 });
    }
  }

  useEffect(() => {
    if (!loading && profile && focusedTicketId) {
      const el = document.getElementById("pcp-sec-complaints");
      if (el) {
        setActiveSection("complaints");
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [loading, profile, focusedTicketId]);

  const openAddReco = () => {
    if (!isLoggedIn) { showToast("Please log in to write a testimonial", "error"); return; }
    if (!isEmployer) { showToast("Only recruiter accounts can write testimonials", "error"); return; }
    if (isOwnProfile) { showToast("You cannot review your own profile", "error"); return; }
    setEditingReco(null);
    setRecoModal(true);
  };
  const openEditReco = (reco) => { setEditingReco(reco); setRecoModal(true); };
  const closeRecoModal = () => { setRecoModal(false); setEditingReco(null); };

  const deleteReco = async (reco) => {
    if (!reco?.id) return;
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/candidate/recommendations/${reco.id}`, { headers: authHeaders() });
      setRecos((prev) => prev.filter((item) => item.id !== reco.id));
      showToast("Testimonial deleted.");
    } catch (e) {
      showToast(e?.response?.data?.error || "Failed to delete.", "error");
    }
  };

  const openRatingModal = () => {
    if (!isLoggedIn) { showToast("Please log in to rate this candidate", "error"); return; }
    if (!isEmployer) { showToast("Only recruiter accounts can submit ratings", "error"); return; }
    if (isOwnProfile) { showToast("You cannot rate your own profile", "error"); return; }
    setRatingModal(true);
  };

  const closeRatingModal = () => {
    setRatingModal(false);
  };

  const copyPublicProfileLink = async () => {
    const url = profile?.username ? getPublicProfileUrl(profile.username) : "";
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showToast("Profile link copied!");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  /* Derived */
  const fullName  = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || "Candidate";
  const initials  = `${profile?.first_name?.[0] || ""}${profile?.last_name?.[0] || ""}`.toUpperCase() || "?";
  const photoSrc  = resolvePhoto(profile?.profile_photo);
  const lv        = getLevelInfo(profile?.reward_points || 0);
  const lvProg    = getLevelProgress(profile?.reward_points || 0);

  const skills    = profile?.skills    || [];
  const education = profile?.education || [];
  const courses   = profile?.courses   || [];
  const projects  = profile?.projects  || [];
  const locations = profile?.locations || [];
  const approvedRecos = recos.filter((r) => String(r.status || "").toLowerCase() === "approved");
  const averageRating = Number(ratingSummary?.average_overall || 0);
  const complaintCount = Number(profile?.complaint_count || complaintSummary?.total || 0);

  const stats = [
    { label: "Skills",   value: skills.length },
    { label: "Courses",  value: courses.length },
    { label: "Degrees",  value: education.length },
    { label: "Projects", value: projects.length },
    { label: "Reviews",  value: approvedRecos.length },
    { label: "Ratings",  value: ratingSummary?.total || ratings.length },
    { label: "Tickets",  value: complaintCount },
  ];

  const navItems = [
    { key: "about",     label: "Overview" },
    { key: "skills",    label: "Expertise",    show: skills.length > 0 },
    { key: "education", label: "Education",    show: education.length > 0 },
    { key: "courses",   label: "Certifications", show: courses.length > 0 },
    { key: "projects",  label: "Projects",     show: projects.length > 0 },
    { key: "recos",     label: "Testimonials" },
    { key: "ratings",   label: "Ratings" },
    { key: "complaints", label: "Tickets" },
  ].filter(n => n.show !== false);

  /* ── Loading / Error states ── */
  if (loading) return (
    <div className="pcp-loader">
      <div className="pcp-loader__ring" />
      <p>Loading profile…</p>
    </div>
  );

  if (error) return (
    <div className="pcp-error-page">
      <div className="pcp-error-page__icon">◎</div>
      <h2>Profile not found</h2>
      <p>{error}</p>
      <button onClick={() => navigate(-1)} className="pcp-btn pcp-btn--primary" style={{ marginTop: 12 }}>
        Go back
      </button>
    </div>
  );

  return (
    <div className="pcp-root">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      {recoModal && (
        <WriteRecoModal
          key={editingReco?.id || "new"}
          candidateId={profile.id}
          candidateName={profile.first_name || "this person"}
          recommendationId={editingReco?.id || null}
          initialComment={editingReco?.comment || ""}
          initialPosition={editingReco?.recommender_position || ""}
          onClose={closeRecoModal}
          onSubmitted={() => {
            loadRecos(profile.id);
            showToast(editingReco ? "Testimonial updated!" : "Testimonial submitted! 🎉");
          }}
        />
      )}

      {ratingModal && (
        <WriteRatingModal
          candidateId={profile.id}
          candidateName={profile.first_name || "this person"}
          onClose={closeRatingModal}
          onSubmitted={() => {
            loadRatings(profile.id);
            showToast("Rating submitted!");
          }}
        />
      )}

      {/* ════════════════ HERO ════════════════ */}

<div className="pcp-hero">
  {/* Ambient orbs */}
  <div className="pcp-hero__orb pcp-hero__orb--1" />
  <div className="pcp-hero__orb pcp-hero__orb--2" />
  <div className="pcp-hero__orb pcp-hero__orb--3" />
 
  <div className="pcp-hero__inner">
    <div className="pcp-hero__main">
 
      {/* ════ LEFT: Large photo ════ */}
      <div className="pcp-hero__photo-col" data-aos="fade-up" data-aos-delay="60">
        <div className="pcp-hero__photo-frame">
          {photoSrc
            ? <img src={photoSrc} alt={fullName} className="pcp-hero__avatar-img" />
            : <div className="pcp-hero__avatar-init">{initials}</div>
          }
          <div className="pcp-hero__avatar-dot" />
        </div>
 
        {/* Mini stat pills below photo */}
        <div className="pcp-hero__photo-stats">
          {stats.slice(0, 3).map((s, i) => (
            <div key={s.label} className="pcp-hero__photo-stat"
              style={{ animationDelay: `${200 + i * 80}ms` }}>
              <div className="pcp-hero__photo-stat-num">
                <AnimCount to={s.value} duration={900 + i * 120} />
              </div>
              <div className="pcp-hero__photo-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
 
      {/* ════ CENTRE: Identity + actions ════ */}
      <div className="pcp-hero__info-col">
 
        {/* Badges row */}
        <div className="pcp-hero__status-row">
          <span className="pcp-badge pcp-badge--level"
            style={{ background: lv.bg, color: lv.color }}>
            {lv.emoji} {lv.title}
          </span>
          {profile?.is_verified && (
            <span className="pcp-badge pcp-badge--verified">✓ KYC Verified</span>
          )}
          {profile?.work_status && (
            <span className="pcp-badge pcp-badge--status pcp-badge--dot">
              {profile.work_status === "fresher" ? "Open to work" : "Experienced"}
            </span>
          )}
          <span className="pcp-badge pcp-badge--status">Public Profile</span>
        </div>
 
        {/* Name */}
        <h1 className="pcp-hero__name">{fullName}</h1>
 
        {profile?.username && (
          <div className="pcp-hero__username">@{profile.username}</div>
        )}
 
        {/* Headline / title */}
        {profile?.headline && (
          <p className="pcp-hero__headline">{profile.headline}</p>
        )}
 
        {/* Location + salary meta */}
        <div className="pcp-hero__meta">
          {profile?.country_iso && (
            <span className="pcp-meta-pill">📍 {profile.country_iso}</span>
          )}
          {locations.length > 0 && (
            <span className="pcp-meta-pill">
              🗺️ Open to: {locations.slice(0, 2).join(", ")}
              {locations.length > 2 ? ` +${locations.length - 2}` : ""}
            </span>
          )}
          {profile?.salary && (
            <span className="pcp-meta-pill">💼 {profile.salary}</span>
          )}
          {skills.length > 0 && (
            <span className="pcp-meta-pill">🛠 {skills.length} skills</span>
          )}
          {approvedRecos.length > 0 && (
            <span className="pcp-meta-pill">💬 {approvedRecos.length} testimonials</span>
          )}
        </div>
 
        {/* Action buttons */}
        <div className="pcp-hero__actions">
          {canRecruiterReview && (
            <button className="pcp-btn pcp-btn--primary" onClick={openAddReco}>
              ✦ Write testimonial
            </button>
          )}
          {canRecruiterReview && (
            <button className="pcp-btn pcp-btn--gold" onClick={openRatingModal}>
              ⭐ Give rating
            </button>
          )}
          {isOwnProfile && (
            <button className="pcp-btn pcp-btn--ghost" onClick={() => navigate("/employee/profile")}>
              Edit profile →
            </button>
          )}
          {profile?.resume_url && (
            <a className="pcp-btn pcp-btn--outline"
              href={profile.resume_url} target="_blank" rel="noreferrer">
              Download CV ↓
            </a>
          )}
          <button className="pcp-btn pcp-btn--ghost" onClick={copyPublicProfileLink}>
            Share ↗
          </button>
        </div>
      </div>
 
      {/* ════ RIGHT: Rating panel ════ */}
      <div className="pcp-hero__rating-col">
 
        {/* Big score card */}
        <div className="pcp-rating-hero-card">
          <div className="pcp-rating-hero-card__label">Overall Rating</div>
          <div className="pcp-rating-hero-card__score">
            {averageRating > 0 ? averageRating.toFixed(1) : "—"}
            <span>/ 5</span>
          </div>
          <div className="pcp-rating-hero-card__stars">
            <StarRow score={averageRating} />
          </div>
          <div className="pcp-rating-hero-card__count">
            {ratingSummary?.total || 0} recruiter rating{(ratingSummary?.total || 0) !== 1 ? "s" : ""}
          </div>
        </div>
 
        {/* Mini bar breakdown — only if ratings exist */}
        {ratingSummary?.total > 0 ? (
          <div className="pcp-rating-mini-bars">
            {[
              ["Skills",        ratingSummary.skills_average],
              ["Experience",    ratingSummary.experience_average],
              ["Communication", ratingSummary.communication_average],
              ["Problem Solving",ratingSummary.problem_solving_average],
              ["Attitude",      ratingSummary.attitude_average],
            ].map(([label, val]) => (
              <div key={label} className="pcp-rating-mini-bar">
                <div className="pcp-rating-mini-bar__row">
                  <span className="pcp-rating-mini-bar__label">{label}</span>
                  <span className="pcp-rating-mini-bar__val">{Number(val || 0).toFixed(1)}</span>
                </div>
                <div className="pcp-rating-mini-bar__track">
                  <div className="pcp-rating-mini-bar__fill"
                    style={{ width: `${Math.min(100, (Number(val || 0) / 5) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pcp-rating-hero-empty">
            <div className="pcp-rating-hero-empty__icon">⭐</div>
            <p>No recruiter ratings yet. Be the first to rate.</p>
          </div>
        )}
 
      </div>{/* end rating-col */}
 
    </div>{/* end hero__main */}
  </div>{/* end hero__inner */}
 
  {/* Wave bottom border */}
  <div className="pcp-hero__wave">
    <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z"
        fill="currentColor" style={{ color: "var(--surface)" }} />
    </svg>
  </div>
</div>

      {/* ════════════════ NAV ════════════════ */}
      <nav className="pcp-nav">
        <div className="pcp-nav__inner">
          {navItems.map(n => (
            <button key={n.key}
              className={`pcp-nav__btn ${activeSection === n.key ? "pcp-nav__btn--active" : ""}`}
              onClick={() => {
                setActiveSection(n.key);
                document.getElementById(`pcp-sec-${n.key}`)?.scrollIntoView({
                  behavior: "smooth", block: "start"
                });
              }}>
              {n.label}
              {n.key === "recos" && approvedRecos.length > 0 && (
                <span className="pcp-nav__count">{approvedRecos.length}</span>
              )}
              {n.key === "ratings" && ratings.length > 0 && (
                <span className="pcp-nav__count">{ratings.length}</span>
              )}
              {n.key === "complaints" && complaintCount > 0 && (
                <span className="pcp-nav__count">{complaintCount}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* ════════════════ BODY ════════════════ */}
      <div className="pcp-body">
        <div className="pcp-container">

          {/* ── ABOUT ── */}
          <RevealSection id="pcp-sec-about" className="pcp-sec">
            <div className="pcp-sec__label">Profile Overview</div>
            <div className="pcp-about-grid">
              {/* Summary card */}
              <div className="pcp-card pcp-card--about" data-aos="fade-up" data-aos-delay="0">
                <div className="pcp-about__deco" />
                <h2 className="pcp-card__title">Professional snapshot</h2>
                {profile?.headline && (
                  <p className="pcp-card__headline">{profile.headline}</p>
                )}
                <div className="pcp-about-facts">
                  {[
                    profile?.gender      && ["Gender",       profile.gender],
                    profile?.country_iso && ["Country",      profile.country_iso],
                    profile?.work_status && ["Status",
                      profile.work_status === "fresher" ? "Fresher / Entry level" : "Experienced professional"],
                    profile?.salary      && ["Expected CTC", profile.salary],
                  ].filter(Boolean).map(([k, v]) => (
                    <div key={k} className="pcp-fact-row">
                      <span className="pcp-fact-row__key">{k}</span>
                      <span className="pcp-fact-row__val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Side cards */}
              <div className="pcp-about-side">
                {/* Level card */}
                <div className="pcp-card pcp-card--level" data-aos="pcp-pop" data-aos-delay="80">
                  <div className="pcp-level-emoji">{lv.emoji}</div>
                  <div className="pcp-level-title">{lv.title}</div>
                  <div className="pcp-level-pts-wrap">
                    <div className="pcp-level-pts">{profile?.reward_points || 0}</div>
                    <div className="pcp-level-pts-unit">pts</div>
                  </div>
                  <div className="pcp-level-sub">TalentHive score</div>
                  <div className="pcp-level-progress">
                    <div className="pcp-level-progress__fill" style={{ width: `${lvProg}%` }} />
                  </div>
                </div>

                {profile?.is_verified && (
                  <div className="pcp-card pcp-card--verified" data-aos="fade-up" data-aos-delay="120">
                    <div className="pcp-card--verified__mark">✓</div>
                    <div>
                      <div className="pcp-card--verified__title">KYC Verified</div>
                      <div className="pcp-card--verified__sub">Identity confirmed</div>
                    </div>
                  </div>
                )}

                {locations.length > 0 && (
                  <div className="pcp-card pcp-card--locations" data-aos="fade-up" data-aos-delay="160">
                    <div className="pcp-card__sec-label">Open to locations</div>
                    <div className="pcp-locations-list">
                      {locations.map(l => (
                        <span key={l} className="pcp-location-chip">{l}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </RevealSection>

          {/* ── SKILLS ── */}
          {skills.length > 0 && (
            <RevealSection id="pcp-sec-skills" className="pcp-sec" delay={80}>
              <div className="pcp-sec__label">Core Expertise</div>
              <div className="pcp-card" data-aos="fade-up">
                <div className="pcp-sec__header">
                  <h2 className="pcp-sec__title">Technical and professional expertise</h2>
                  <span className="pcp-sec__count">{skills.length} skills</span>
                </div>
                <div className="pcp-skills-wrap">
                  {skills.map((s, i) => (
                    <SkillPill key={s} skill={s} delay={i * 55} />
                  ))}
                </div>
              </div>
            </RevealSection>
          )}

          {/* ── EDUCATION ── */}
          {education.length > 0 && (
            <RevealSection id="pcp-sec-education" className="pcp-sec" delay={100}>
              <div className="pcp-sec__label">Education Timeline</div>
              <div className="pcp-edu-tree">
                <div className="pcp-edu-tree__rail" />
                <div className="pcp-edu-list">
                  {education.map((edu, i) => (
                    <div key={i} className={`pcp-edu-card ${i % 2 === 0 ? "pcp-edu-card--left" : "pcp-edu-card--right"}`}
                      data-aos="fade-up" data-aos-delay={`${i * 80}`}>
                      <div className="pcp-edu-card__spacer" />
                      <div className="pcp-edu-card__rail">
                        <span className="pcp-edu-card__node" />
                        {i !== education.length - 1 && <span className="pcp-edu-card__line" />}
                      </div>
                      <div className="pcp-edu-card__body">
                        <div className="pcp-edu-card__degree">
                          {edu.degree}
                          {edu.course && (
                            <span className="pcp-edu-card__course"> — {edu.course}</span>
                          )}
                        </div>
                        {edu.university && (
                          <div className="pcp-edu-card__uni">{edu.university}</div>
                        )}
                        {edu.specialization && (
                          <div className="pcp-edu-card__spec">{edu.specialization}</div>
                        )}
                        <div className="pcp-edu-card__meta">
                          {(edu.start_year || edu.passing_year) && (
                            <span>📅 {edu.start_year || "?"} — {edu.passing_year || "Present"}</span>
                          )}
                          {edu.percentage && (
                            <span className="pcp-edu-card__badge">{edu.percentage}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
          )}

          {/* ── COURSES ── */}
          {education.length === 0 && (
            <RevealSection id="pcp-sec-education" className="pcp-sec" delay={100}>
              <div className="pcp-sec__label">Education Timeline</div>
              <div className="pcp-edu-empty">
                <div className="pcp-edu-empty__icon">🎓</div>
                <p>No education details have been added yet.</p>
              </div>
            </RevealSection>
          )}

          {courses.length > 0 && (
            <RevealSection id="pcp-sec-courses" className="pcp-sec" delay={120}>
              <div className="pcp-sec__label">Certifications & Courses</div>
              <div className="pcp-card" data-aos="fade-up">
                <div className="pcp-sec__header">
                  <h2 className="pcp-sec__title">Professional development</h2>
                  <span className="pcp-sec__count">{courses.length}</span>
                </div>
                <div className="pcp-courses-grid">
                  {courses.map((c, i) => (
                    <div key={i} className="pcp-course-card"
                      style={{ animationDelay: `${i * 70}ms` }}>
                      <div className="pcp-course-card__dot" />
                      <div className="pcp-course-card__name">{c}</div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
          )}

          {/* ── PROJECTS ── */}
          {projects.length > 0 && (
            <RevealSection id="pcp-sec-projects" className="pcp-sec" delay={130}>
              <div className="pcp-sec__label">Projects</div>
              <div className="pcp-card" data-aos="fade-up">
                <div className="pcp-sec__header">
                  <h2 className="pcp-sec__title">Portfolio projects</h2>
                  <span className="pcp-sec__count">{projects.length}</span>
                </div>
                <div className="pcp-projects-grid">
                  {projects.map((project, i) => (
                    <div key={`${project.title || i}-${i}`} className="pcp-project-card" data-aos="fade-up" data-aos-delay={`${i * 80}`}>
                      <div className="pcp-project-card__media">
                        {project.image_url ? (
                          <img src={project.image_url} alt={project.title || "Project image"} />
                        ) : (
                          <div className="pcp-project-card__media-fallback">🧩</div>
                        )}
                      </div>
                      <div className="pcp-project-card__body">
                        <div className="pcp-project-card__title">{project.title || "Untitled project"}</div>
                        {project.description && (
                          <p className="pcp-project-card__desc">{project.description}</p>
                        )}
                        {Array.isArray(project.technologies) && project.technologies.length > 0 && (
                          <div className="pcp-project-card__chips">
                            {project.technologies.map((tech) => (
                              <span key={tech} className="pcp-project-card__chip">{tech}</span>
                            ))}
                          </div>
                        )}
                        {project.project_url && (
                          <a className="pcp-project-card__link" href={project.project_url} target="_blank" rel="noreferrer">
                            View project →
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealSection>
          )}

          {/* ── RECOMMENDATIONS ── */}
          <RevealSection id="pcp-sec-recos" className="pcp-sec" delay={140}>
            <div className="pcp-sec__label">Testimonials</div>
            <div className="pcp-recos-header">
              <div>
                <h2 className="pcp-sec__title">Professional testimonials</h2>
                {approvedRecos.length > 0 && (
                  <p className="pcp-recos-sub">
                    {approvedRecos.length} testimonial{approvedRecos.length !== 1 ? "s" : ""}
                  </p>
                )}
              </div>
              {canRecruiterReview && (
                <button className="pcp-btn pcp-btn--primary" onClick={openAddReco}>
                  ✦ Add testimonial
                </button>
              )}
            </div>

            {approvedRecos.length === 0 ? (
              <div className="pcp-recos-empty">
                <div className="pcp-recos-empty__icon">💬</div>
                <p>No approved testimonials yet.</p>
                {canRecruiterReview && (
                  <button className="pcp-btn pcp-btn--primary" onClick={openAddReco}
                    style={{ marginTop: 8 }}>
                    Be the first to write a testimonial
                  </button>
                )}
                {!canRecruiterReview && !isOwnProfile && (
                  <p className="pcp-recos-empty__hint">Only recruiter accounts can add testimonials.</p>
                )}
              </div>
            ) : approvedRecos.length > 3 ? (
              <div className="pcp-recos-marquee" aria-label="Testimonials carousel">
                <div className="pcp-recos-marquee__track">
                  {[...approvedRecos, ...approvedRecos].map((r, i) => (
                    <div className="pcp-recos-marquee__item" key={`${r.id}-${i}`}>
                      <RecoCard reco={r} delay={0} onEdit={openEditReco} onDelete={deleteReco} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="pcp-recos-grid">
                {approvedRecos.map((r, i) => (
                  <RecoCard key={r.id} reco={r} delay={i * 100}
                    onEdit={openEditReco} onDelete={deleteReco} />
                ))}
              </div>
            )}
          </RevealSection>

          {/* â”€â”€ RATINGS â”€â”€ */}
          <RevealSection id="pcp-sec-ratings" className="pcp-sec" delay={150}>
            <div className="pcp-sec__label">Ratings</div>
            <div className="pcp-card" data-aos="fade-up">
              <div className="pcp-sec__header">
                <h2 className="pcp-sec__title">Recruiter rating summary</h2>
                <span className="pcp-sec__count">{ratingSummary?.total || ratings.length} rating{(ratingSummary?.total || ratings.length) !== 1 ? "s" : ""}</span>
              </div>

              {ratingSummary?.total > 0 ? (
                <div className="pcp-ratings-summary">
                  <div className="pcp-ratings-summary__score">
                    <div className="pcp-ratings-summary__num">{averageRating.toFixed(1)}</div>
                    <div className="pcp-ratings-summary__label">Average overall</div>
                  </div>
                  <div className="pcp-ratings-summary__bars">
                    {[
                      ["Skills", ratingSummary.skills_average],
                      ["Experience", ratingSummary.experience_average],
                      ["Communication", ratingSummary.communication_average],
                      ["Problem Solving", ratingSummary.problem_solving_average],
                      ["Attitude", ratingSummary.attitude_average],
                    ].map(([label, value]) => (
                      <div key={label} className="pcp-rating-bar">
                        <div className="pcp-rating-bar__row">
                          <span>{label}</span>
                          <strong>{Number(value || 0).toFixed(1)}</strong>
                        </div>
                        <div className="pcp-rating-bar__track">
                          <div className="pcp-rating-bar__fill" style={{ width: `${Math.max(0, Math.min(100, (Number(value || 0) / 5) * 100))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="pcp-recos-empty" style={{ marginTop: 0 }}>
                  <div className="pcp-recos-empty__icon">⭐</div>
                  <p>No ratings yet.</p>
                  {canRecruiterReview && (
                    <button className="pcp-btn pcp-btn--primary" onClick={openRatingModal} style={{ marginTop: 8 }}>
                      Give the first rating
                    </button>
                  )}
                </div>
              )}

              {ratings.length > 0 && (
                <div className="pcp-rating-cards">
                  {ratings.map((rating) => (
                    <div key={rating.id} className="pcp-rating-card">
                      <div className="pcp-rating-card__head">
                        <div>
                          <div className="pcp-rating-card__name">{rating.reviewer_name || "Recruiter"}</div>
                          <div className="pcp-rating-card__meta">
                            {rating.reviewer_position || "Recruiter"}
                            {rating.reviewer_company ? ` · ${rating.reviewer_company}` : ""}
                          </div>
                        </div>
                        <div className="pcp-rating-card__overall">{Number(rating.overall_rating || 0).toFixed(1)} / 5</div>
                      </div>
                      {rating.note && <p className="pcp-rating-card__note">{rating.note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </RevealSection>

          {/* â”€â”€ COMPLAINTS â”€â”€ */}
          <RevealSection id="pcp-sec-complaints" className="pcp-sec" delay={160}>
            <div className="pcp-sec__label">Tickets</div>
            <div className="pcp-card" data-aos="fade-up">
              <div className="pcp-sec__header">
                <h2 className="pcp-sec__title">Complaint tickets</h2>
                <span className="pcp-sec__count">{complaintCount} ticket{complaintCount !== 1 ? "s" : ""}</span>
              </div>

              <div className="pcp-ratings-summary" style={{ marginBottom: 18 }}>
                <div className="pcp-ratings-summary__score">
                  <div className="pcp-ratings-summary__num">{complaintSummary.open || 0}</div>
                  <div className="pcp-ratings-summary__label">Open</div>
                </div>
                <div className="pcp-ratings-summary__score">
                  <div className="pcp-ratings-summary__num">{complaintSummary.resolved || 0}</div>
                  <div className="pcp-ratings-summary__label">Resolved</div>
                </div>
                <div className="pcp-ratings-summary__score">
                  <div className="pcp-ratings-summary__num">{complaintSummary.hidden || 0}</div>
                  <div className="pcp-ratings-summary__label">Hidden</div>
                </div>
              </div>

              {!canSeeComplaintDetails ? (
                <div className="pcp-recos-empty" style={{ marginTop: 0 }}>
                  <div className="pcp-recos-empty__icon">ðŸ›¡ï¸</div>
                  <p>Ticket details are visible only to the candidate and admin.</p>
                  <p className="pcp-recos-empty__hint">This profile currently has {complaintCount} recorded ticket{complaintCount !== 1 ? "s" : ""}.</p>
                </div>
              ) : complaintLoading ? (
                <div className="pcp-recos-empty" style={{ marginTop: 0 }}>
                  <div className="pcp-loader__ring" />
                  <p>Loading ticket details…</p>
                </div>
              ) : complaints.length === 0 ? (
                <div className="pcp-recos-empty" style={{ marginTop: 0 }}>
                  <div className="pcp-recos-empty__icon">ðŸ›¡ï¸</div>
                  <p>No open complaint tickets right now.</p>
                  <p className="pcp-recos-empty__hint">Resolved or hidden tickets do not appear on the candidate profile.</p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14 }}>
                  {complaints.map((ticket) => {
                    const isFocused = focusedTicketId && Number(ticket.id) === focusedTicketId;
                    return (
                      <div
                        key={ticket.id}
                        className="pcp-card"
                        style={{
                          border: isFocused ? "1.5px solid #4338ca" : "1px solid #e2e8f0",
                          background: isFocused ? "#eef2ff" : "white",
                        }}
                      >
                        <div className="pcp-sec__header" style={{ marginBottom: 10 }}>
                          <div>
                            <h3 className="pcp-sec__title" style={{ fontSize: 18 }}>{ticket.title}</h3>
                            <p className="pcp-recos-sub" style={{ marginTop: 4 }}>
                              {ticket.recruiter_company || ticket.recruiter_name || "Recruiter"} · {new Date(ticket.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          </div>
                          <span className="pcp-badge pcp-badge--status">{String(ticket.status || "open").replace(/_/g, " ")}</span>
                        </div>

                        <div className="pcp-hero__badges" style={{ marginBottom: 12 }}>
                          <span className="pcp-badge" style={{ background: "#f8fafc", color: "#475569" }}>{String(ticket.category || "misconduct").replace(/_/g, " ")}</span>
                          {(ticket.proof_url || ticket.proof_path) && (
                            <a className="pcp-badge" href={resolveApiFileUrl(ticket.proof_url || ticket.proof_path)} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                              Open proof
                            </a>
                          )}
                          {ticket.visible_on_profile ? (
                            <span className="pcp-badge" style={{ background: "#f0fdf4", color: "#16a34a" }}>Visible on profile</span>
                          ) : (
                            <span className="pcp-badge" style={{ background: "#fef2f2", color: "#dc2626" }}>Hidden from profile</span>
                          )}
                        </div>

                        <p className="pcp-card__headline" style={{ whiteSpace: "pre-wrap" }}>
                          {ticket.message || "No message provided."}
                        </p>

                        {(ticket.hidden_reason || ticket.admin_note) && (
                          <div className="pcp-about-facts" style={{ marginTop: 14 }}>
                            {ticket.hidden_reason && (
                              <div className="pcp-fact-row">
                                <span className="pcp-fact-row__key">Hide reason</span>
                                <span className="pcp-fact-row__val">{ticket.hidden_reason}</span>
                              </div>
                            )}
                            {ticket.admin_note && (
                              <div className="pcp-fact-row">
                                <span className="pcp-fact-row__key">Admin note</span>
                                <span className="pcp-fact-row__val">{ticket.admin_note}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </RevealSection>

        </div>
      </div>

      {/* ════════════════ FOOTER ════════════════ */}
      <div className="pcp-footer">
        <span>Profile powered by</span>
        <strong>TalentHive</strong>
        <div className="pcp-footer__dot" />
        <a href="/employee/jobs">Browse jobs</a>
        {!isLoggedIn && (
          <>
            <div className="pcp-footer__dot" />
            <a href="/employee/login">Log in</a>
          </>
        )}
      </div>
    </div>
  );
}
